'use client';

import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import classnames from 'classnames';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import React, { Dispatch, Fragment, KeyboardEvent, SetStateAction, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';

import { ActionIcon } from '../button/ActionIcon';
import { withOpacity } from '../helpers/color-helper';
import { IconClose24 } from '../icons/ic-close';
import { isMdxPopupOpenOrJustClosed } from './mdx-popup-state';
import { TooltipDirection } from '../tooltip/Tooltip';

export type ModalProps = {
    children?: React.ReactNode;
    classNameDialog?: string;
    className?: string;
    styleDialogPanel?: CSSProperties;
    /** @deprecated Use classNameDialog */
    zIndex?: number;
    title?: string | React.JSX.Element;
    titleCenter?: boolean;
    titleClassName?: string;
    subTitle?: string | React.JSX.Element;
    subTitleClassName?: string;
    modalIsOpen?: boolean;
    setModalIsOpen?: Dispatch<SetStateAction<boolean>>;
    closeable?: boolean;
    showCloseButton?: boolean;
    showBackground?: boolean;
    showBackgroundBlur?: boolean;
    showBackgroundImage?: boolean;
    backgroundColor?: string;
    enableOutsideActions?: boolean;
    /** `null` suppresses the default close (router.back()); `undefined` keeps it. */
    onClose?: (() => void) | null;
    beforeClose?: () => boolean | void;
    allowEnter?: boolean;
    trailingElements?: React.JSX.Element;
    /**
     * Optional footer rendered OUTSIDE the scrollable body, as a direct child of the
     * padded panel. Stays fixed while the body scrolls, and (unlike body content) its
     * children may break out to the full panel width via negative horizontal margins.
     */
    footer?: React.ReactNode;
};

function Modal(props: ModalProps): React.JSX.Element | null {
    const { classNameDialog, className, styleDialogPanel, onClose, beforeClose, allowEnter } = props;

    const t = useTranslations();

    const router = useRouter();

    let modalIsOpen = props?.modalIsOpen;
    let setModalIsOpen = props?.setModalIsOpen;
    const [state, setState] = useState<boolean>(true);
    if (modalIsOpen === undefined && setModalIsOpen === undefined) {
        modalIsOpen = state;
        setModalIsOpen = setState;
    }

    const closeable = props.closeable !== false;

    const showCloseButton = closeable && props.showCloseButton !== false;

    const trailingElements: React.JSX.Element[] = useMemo(() => {
        const elements: React.JSX.Element[] = [];
        if (props.trailingElements) {
            elements.push(props.trailingElements);
        }
        return elements;
    }, [props.trailingElements, closeable]);

    function onCloseInternal() {
        // An MDX editor popup on screen, or one dismissed a moment ago, means this close request
        // came from that popup and not from the operator leaving — see mdx-popup-state. Inert for
        // every modal without such an editor, since then no popup container exists.
        if (isMdxPopupOpenOrJustClosed()) {
            return;
        }
        if (beforeClose && beforeClose() === false) {
            return;
        }
        setModalIsOpen?.(false);
        if (onClose) {
            onClose();
        } else if (onClose === undefined) {
            router.back();
        }
    }

    if (modalIsOpen === false) {
        return null;
    }

    return (
        <Dialog
            open={modalIsOpen}
            onClose={() => {
                if (closeable) {
                    onCloseInternal();
                }
            }}
            onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
                if (allowEnter || e.key !== 'Enter') {
                    return;
                }
                // Only suppress the implicit form-submit that a bare Enter triggers from within a
                // single-line text input. Enter must still activate a focused button, link, etc.
                const target = e.target as HTMLElement;
                if (target.tagName === 'INPUT') {
                    const inputType = (target as HTMLInputElement).type;
                    const nonSubmittingTypes = ['button', 'submit', 'reset', 'checkbox', 'radio', 'file'];
                    if (!nonSubmittingTypes.includes(inputType)) {
                        e.preventDefault();
                    }
                }
            }}
            className={classnames('relative z-[9000]', classNameDialog, {
                'pointer-events-none': props.enableOutsideActions
            })}>
            <DialogBackdrop
                className={classnames('fixed inset-0 transition-all duration-300', {
                    'bg-overlay': props.showBackground !== false,
                    'backdrop-blur-sm': props.showBackgroundBlur !== false,
                    [props.backgroundColor || '']: !props.backgroundColor?.startsWith('#')
                })}
                style={{
                    backgroundColor: props.backgroundColor?.startsWith('#')
                        ? (withOpacity(props.backgroundColor, 0.8) ?? undefined)
                        : undefined
                }}
            />

            <div className="fixed inset-0 flex w-screen items-center justify-center p-[9px] md:p-24">
                <DialogPanel
                    className={classnames(
                        className,
                        'relative bg-[var(--non-transparent-surface-color)] flex flex-col text-[var(--on-non-transparent-surface-color)]',
                        'w-full',
                        'min-h-[120px] max-h-[100%] overflow-hidden',
                        'flex flex-col p-[16px] md:p-[24px]',
                        'modal-window',
                        'rounded-8 relative',
                        {
                            'pointer-events-auto': props.enableOutsideActions
                        }
                    )}
                    style={styleDialogPanel}>
                    {(props.title || props.subTitle || showCloseButton) && (
                        <DialogTitle className="flex flex-col pb-[18px] md:pb-[16px] space-y-[16px]">
                            <div className={classnames('flex flex-row flex-none')}>
                                <div
                                    className={classnames('flex flex-col flex-auto', {
                                        'text-center mx-auto': props.titleCenter
                                    })}>
                                    {props.title && (
                                        <span
                                            className={classnames(
                                                props.titleClassName || 'text-headline-20 lg:text-headline-24'
                                            )}>
                                            {props.title}
                                        </span>
                                    )}
                                    {props.subTitle && (
                                        <div
                                            className={classnames(
                                                props.subTitleClassName ||
                                                    'text-body-bold-14 lg:text-headline-18 tracking-wide text-[rgba(var(--on-non-transparent-surface-color-rgb),.5)]'
                                            )}>
                                            {props.subTitle}
                                        </div>
                                    )}
                                </div>

                                {showCloseButton && (
                                    <div
                                        className="text-[var(--on-non-transparent-surface-color)] opacity-50 self-start"
                                        key={'close'}>
                                        <ActionIcon
                                            icon={<IconClose24 />}
                                            action={onCloseInternal}
                                            tooltipDirection={TooltipDirection.Top}
                                            tooltipText={t('common.close')}
                                        />
                                    </div>
                                )}
                            </div>

                            {trailingElements?.length > 0 && (
                                <div className="flex flex-row justify-end space-x-24 items-center">
                                    {trailingElements.map((element, index) => (
                                        <Fragment key={index}>{element}</Fragment>
                                    ))}
                                </div>
                            )}
                        </DialogTitle>
                    )}

                    <div className="w-full h-auto flex flex-col max-h-full overflow-y-auto flex-auto">
                        {props.children}
                    </div>

                    {props.footer && <div className="flex-none">{props.footer}</div>}
                </DialogPanel>
            </div>
        </Dialog>
    );
}

export default Modal;
